/**
 * Heute — die Lande-Seite des Leitstands (Einrichten-Neubau P1).
 *
 * Ersetzt das Dashboard und beantwortet genau eine Frage: Läuft der Tag —
 * und was braucht mich? Laufende Schicht, Erfassungsstand live, und die
 * Aufmerksamkeits-Liste (Störungen, Korrekturen, Schichtplan, Meldungen) —
 * jede Zeile springt zur zuständigen Seite, bearbeitet wird hier nichts.
 * Vortags-Prozente und Top-Listen wohnen bewusst unter Auswerten.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../App';
import { LeitstandRahmen } from '../components/leitstand/LeitstandRahmen';
import { Karte, Kennzahl, Notiz, Zustand } from '../components/leitstand';
import type { ZustandsArt } from '../components/leitstand';

interface HeuteDaten {
  datum: string;
  schicht: {
    hasActiveShift: boolean;
    shiftTypeName: string | null;
    startTime: string | null;
    endTime: string | null;
  };
  wsa: { kg: number; anzahl: number };
  fl: { kg: number; anzahl: number };
  letzteErfassung: { vorMinuten: number; text: string } | null;
  stoerungen: { laufend: number; langlaeufer: number };
  korrekturen: { offen: number; aeltesteTage: number };
  meldungen: { offen: number };
  planungshorizont: { letzterTag: string | null; tageVoraus: number; knapp: boolean };
}

function vorText(minuten: number): string {
  if (minuten === 0) return 'gerade eben';
  if (minuten < 60) return `vor ${minuten} min`;
  if (minuten < 36 * 60) return `vor ${Math.round(minuten / 60)} h`;
  return `vor ${Math.round(minuten / 1440)} Tagen`;
}

function kg(wert: number): string {
  return wert.toLocaleString('de-DE', { maximumFractionDigits: 0 });
}

function datumDE(iso: string): string {
  return new Date(`${iso}T12:00:00Z`).toLocaleDateString('de-DE', {
    weekday: 'long',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

interface AufmerksamkeitsZeile {
  schluessel: string;
  art: ZustandsArt | 'aus';
  etikett: string;
  text: React.ReactNode;
  ziel: string;
  zielName: string;
}

export default function HeuteBetrieb() {
  const { user } = useAuth();
  const [daten, setDaten] = useState<HeuteDaten | null>(null);
  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState('');
  const [stand, setStand] = useState('');

  const lade = useCallback(async () => {
    setLaden(true);
    setFehler('');
    try {
      const res = await fetch('/api/reports/betrieb-heute', { credentials: 'include' });
      if (!res.ok) throw new Error('Die Heute-Übersicht konnte nicht geladen werden');
      setDaten(await res.json());
      setStand(
        new Date().toLocaleTimeString('de-DE', {
          timeZone: 'Europe/Berlin',
          hour: '2-digit',
          minute: '2-digit',
        }),
      );
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Die Heute-Übersicht konnte nicht geladen werden');
    } finally {
      setLaden(false);
    }
  }, []);

  useEffect(() => {
    void lade();
    const timer = setInterval(() => void lade(), 60 * 1000);
    return () => clearInterval(timer);
  }, [lade]);

  const zeilen: AufmerksamkeitsZeile[] = [];
  if (daten) {
    if (daten.stoerungen.laufend > 0) {
      zeilen.push({
        schluessel: 'stoerungen',
        art: 'stoerung',
        etikett: 'Störung',
        text: (
          <>
            <b>
              {daten.stoerungen.laufend === 1
                ? 'Eine laufende Störung'
                : `${daten.stoerungen.laufend} laufende Störungen`}
            </b>
            {daten.stoerungen.langlaeufer > 0 && (
              <span className="text-tinte-3">
                {' '}
                · {daten.stoerungen.langlaeufer} länger als 12 h — vermutlich vergessen
              </span>
            )}
          </>
        ),
        ziel: '/admin/disruptions',
        zielName: 'Betrieb → Störungen',
      });
    }
    if (daten.korrekturen.offen > 0) {
      zeilen.push({
        schluessel: 'korrekturen',
        art: 'warnung',
        etikett: 'Korrektur',
        text: (
          <>
            <b>
              {daten.korrekturen.offen === 1 ? 'Ein Antrag' : `${daten.korrekturen.offen} Anträge`}
            </b>{' '}
            warten auf Prüfung
            {daten.korrekturen.aeltesteTage > 0 && (
              <span className="text-tinte-3">
                {' '}
                · ältester seit {daten.korrekturen.aeltesteTage}{' '}
                {daten.korrekturen.aeltesteTage === 1 ? 'Tag' : 'Tagen'}
              </span>
            )}
          </>
        ),
        ziel: '/admin/corrections',
        zielName: 'Betrieb → Korrekturen',
      });
    }
    // Der Schichtplan entsteht nicht mehr von selbst — er wird geplant.
    // Deshalb genügt eine Frage: Wie weit reicht er noch?
    if (daten.planungshorizont.knapp) {
      zeilen.push({
        schluessel: 'planungshorizont',
        art: 'warnung',
        etikett: 'Schichtplan',
        text: daten.planungshorizont.letzterTag ? (
          <>
            Geplant bis <b>{daten.planungshorizont.letzterTag.split('-').reverse().join('.')}</b> —
            danach ist nichts geplant
          </>
        ) : (
          <>
            Für die kommenden Tage ist <b>keine Schicht geplant</b>
          </>
        ),
        ziel: '/admin/shifts',
        zielName: 'Betrieb → Schichten',
      });
    }
    if (daten.meldungen.offen > 0) {
      zeilen.push({
        schluessel: 'meldungen',
        art: 'aus',
        etikett: 'Meldung',
        text: (
          <>
            <b>
              {daten.meldungen.offen === 1
                ? 'Eine offene Meldung'
                : `${daten.meldungen.offen} offene Meldungen`}
            </b>
          </>
        ),
        ziel: '/admin/feedback',
        zielName: 'Betrieb → Meldungen',
      });
    }
  }

  return (
    <LeitstandRahmen
      titel="Heute"
      benutzer={user?.displayName}
      werkzeuge={
        daten && (
          <span className="font-zahl text-xs text-tinte-3">
            {datumDE(daten.datum)} · Stand {stand}
          </span>
        )
      }
    >
      {fehler && (
        <Notiz titel="Konnte nicht laden">
          {fehler}{' '}
          <button onClick={() => void lade()} className="underline">
            Erneut versuchen
          </button>
        </Notiz>
      )}

      {laden && !daten && !fehler && (
        <p className="py-12 text-center text-sm text-tinte-3">Lade den Tag …</p>
      )}

      {daten && (
        <div className="flex flex-col gap-6">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Kennzahl
              marke="Laufende Schicht"
              klein
              wert={
                daten.schicht.hasActiveShift
                  ? (daten.schicht.shiftTypeName ?? '—')
                  : 'Keine Schicht'
              }
              neben={
                daten.schicht.hasActiveShift && daten.schicht.startTime
                  ? `${daten.schicht.startTime} – ${daten.schicht.endTime}`
                  : 'außerhalb der Schichtzeiten'
              }
            />
            <Kennzahl
              marke="WSA heute"
              wert={kg(daten.wsa.kg)}
              einheit="kg"
              neben={`${daten.wsa.anzahl} Waschgänge`}
            />
            <Kennzahl
              marke="Strecken heute"
              wert={kg(daten.fl.kg)}
              einheit="kg"
              neben={`${daten.fl.anzahl} Erfassungen`}
            />
            <Kennzahl
              marke="Letzte Erfassung"
              klein
              wert={daten.letzteErfassung ? vorText(daten.letzteErfassung.vorMinuten) : '—'}
              neben={daten.letzteErfassung?.text}
            />
          </div>

          <Karte titel="Braucht Aufmerksamkeit" marke="jede Zeile springt zur zuständigen Seite">
            {zeilen.length === 0 ? (
              <p className="py-6 text-center text-sm text-tinte-3">
                Nichts wartet — keine laufende Störung, keine offenen Anträge, der Schichtplan
                reicht.
              </p>
            ) : (
              <div className="flex flex-col">
                {zeilen.map((z, i) => (
                  <Link
                    key={z.schluessel}
                    to={z.ziel}
                    className={`flex items-center gap-4 px-1 py-3 hover:bg-flaeche-2 ${
                      i < zeilen.length - 1 ? 'border-b border-linie' : ''
                    }`}
                  >
                    {z.art === 'aus' ? (
                      <span className="inline-flex items-center gap-1.5 font-zahl text-[11px] text-tinte-3">
                        <i className="h-[7px] w-[7px] rounded-full bg-current" aria-hidden />
                        {z.etikett}
                      </span>
                    ) : (
                      <Zustand art={z.art}>{z.etikett}</Zustand>
                    )}
                    <span className="min-w-0 text-sm">{z.text}</span>
                    <span className="ml-auto whitespace-nowrap text-[12.5px] text-tinte-3">
                      {z.zielName}
                    </span>
                    <span className="font-zahl text-tinte-3">→</span>
                  </Link>
                ))}
              </div>
            )}
          </Karte>

          <p className="text-[12.5px] text-tinte-3">
            Hier wird nichts bearbeitet — die Seite zeigt den Tag und führt dorthin, wo gehandelt
            wird. Zahlen zum Vergleichen wohnen unter Auswerten.
          </p>
        </div>
      )}
    </LeitstandRahmen>
  );
}
